const GRID_COLUMNS = 12;
const GRID_ROWS = 8;
const MAX_SAMPLES = 250_000;
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 4) => Number(value.toFixed(digits));
function isGreen(r, g, b) {
    return g >= 40 && g - Math.max(r, b) >= 20 && g >= r * 1.25 && g >= b * 1.1;
}
function isBlue(r, g, b) {
    return b >= 40 && b - Math.max(r, g) >= 20 && b >= r * 1.25 && b >= g * 1.1;
}
function isScreenPixel(type, r, g, b) {
    return type === 'green' ? isGreen(r, g, b) : type === 'blue' ? isBlue(r, g, b) : false;
}
function cellOverlay(index, label, color) {
    const column = index % GRID_COLUMNS;
    const row = Math.floor(index / GRID_COLUMNS);
    return {
        type: 'box',
        points: [
            { x: column / GRID_COLUMNS, y: row / GRID_ROWS },
            { x: (column + 1) / GRID_COLUMNS, y: (row + 1) / GRID_ROWS },
        ],
        label,
        color,
        opacity: 0.8,
        stroke_width: 2,
        fill: true,
    };
}
/** Classical pixel and spatial-luminance analysis for green/blue screen quality. */
export class ScreenQualityAnalyzer {
    id = 'screen-quality';
    label = 'Screen Quality';
    analyze(frame, context) {
        const { width, height, data } = frame;
        const pixelCount = width * height;
        const stride = Math.max(1, Math.ceil(Math.sqrt(pixelCount / MAX_SAMPLES)));
        let sampledPixels = 0;
        let greenPixels = 0;
        let bluePixels = 0;
        for (let y = 0; y < height; y += stride) {
            for (let x = 0; x < width; x += stride) {
                const offset = (y * width + x) * 4;
                const r = data[offset] ?? 0;
                const g = data[offset + 1] ?? 0;
                const b = data[offset + 2] ?? 0;
                sampledPixels++;
                if (isGreen(r, g, b))
                    greenPixels++;
                if (isBlue(r, g, b))
                    bluePixels++;
            }
        }
        const dominantCount = Math.max(greenPixels, bluePixels);
        const runnerUpCount = Math.min(greenPixels, bluePixels);
        const dominantCoverage = sampledPixels === 0 ? 0 : dominantCount / sampledPixels;
        const screenType = dominantCoverage < 0.12
            ? 'unknown'
            : greenPixels > bluePixels ? 'green' : bluePixels > greenPixels ? 'blue' : 'unknown';
        const cells = Array.from({ length: GRID_COLUMNS * GRID_ROWS }, () => ({ chromaCount: 0, sampleCount: 0, lumaSum: 0 }));
        let chromaCount = 0;
        let sumR = 0;
        let sumG = 0;
        let sumB = 0;
        let sumR2 = 0;
        let sumG2 = 0;
        let sumB2 = 0;
        let lumaSum = 0;
        let minX = width;
        let minY = height;
        let maxX = 0;
        let maxY = 0;
        for (let y = 0; y < height; y += stride) {
            for (let x = 0; x < width; x += stride) {
                const offset = (y * width + x) * 4;
                const r = data[offset] ?? 0;
                const g = data[offset + 1] ?? 0;
                const b = data[offset + 2] ?? 0;
                const column = Math.min(GRID_COLUMNS - 1, Math.floor((x / width) * GRID_COLUMNS));
                const row = Math.min(GRID_ROWS - 1, Math.floor((y / height) * GRID_ROWS));
                const cell = cells[row * GRID_COLUMNS + column];
                cell.sampleCount++;
                if (!isScreenPixel(screenType, r, g, b))
                    continue;
                const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                chromaCount++;
                sumR += r;
                sumG += g;
                sumB += b;
                sumR2 += r * r;
                sumG2 += g * g;
                sumB2 += b * b;
                lumaSum += luma;
                cell.chromaCount++;
                cell.lumaSum += luma;
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
            }
        }
        const coverage = sampledPixels === 0 ? 0 : chromaCount / sampledPixels;
        const meanR = chromaCount ? sumR / chromaCount : 0;
        const meanG = chromaCount ? sumG / chromaCount : 0;
        const meanB = chromaCount ? sumB / chromaCount : 0;
        const varianceR = chromaCount ? Math.max(0, sumR2 / chromaCount - meanR ** 2) : 0;
        const varianceG = chromaCount ? Math.max(0, sumG2 / chromaCount - meanG ** 2) : 0;
        const varianceB = chromaCount ? Math.max(0, sumB2 / chromaCount - meanB ** 2) : 0;
        const colorDeviation = Math.sqrt((varianceR + varianceG + varianceB) / 3);
        const uniformityScore = screenType === 'unknown' ? 0 : clamp01(1 - colorDeviation / 90);
        const meanLuma = chromaCount ? lumaSum / chromaCount : 0;
        const activeCells = cells
            .map((cell, index) => ({
            index,
            meanLuma: cell.chromaCount ? cell.lumaSum / cell.chromaCount : 0,
            coverage: cell.sampleCount ? cell.chromaCount / cell.sampleCount : 0,
        }))
            .filter((cell) => cell.coverage >= 0.35);
        const cellLumaMean = activeCells.length
            ? activeCells.reduce((sum, cell) => sum + cell.meanLuma, 0) / activeCells.length
            : meanLuma;
        const cellLumaDeviation = activeCells.length
            ? Math.sqrt(activeCells.reduce((sum, cell) => sum + (cell.meanLuma - cellLumaMean) ** 2, 0) / activeCells.length)
            : 0;
        const lightingUniformityScore = screenType === 'unknown' ? 0 : clamp01(1 - cellLumaDeviation / 70);
        const lightingThreshold = Math.max(18, cellLumaMean * 0.15);
        const hotspotCells = activeCells.filter((cell) => cell.meanLuma > cellLumaMean + lightingThreshold);
        const darkCells = activeCells.filter((cell) => cell.meanLuma < cellLumaMean - lightingThreshold);
        const unevenLighting = screenType !== 'unknown' && activeCells.length > 1 &&
            cellLumaDeviation / Math.max(1, cellLumaMean) > 0.12;
        const coverageScore = clamp01(coverage / 0.8);
        const screenQualityScore = screenType === 'unknown'
            ? 0
            : clamp01(coverageScore * 0.35 + uniformityScore * 0.4 + lightingUniformityScore * 0.25);
        const confidence = screenType === 'unknown'
            ? clamp01(1 - dominantCoverage * 2)
            : clamp01(coverage * 0.6 + ((dominantCount - runnerUpCount) / Math.max(1, dominantCount)) * 0.4);
        const warnings = [];
        const warning = (code, message) => {
            warnings.push({ code, severity: 'warning', message, module_id: this.id, frame_number: context.frameNumber });
        };
        if (screenType === 'unknown')
            warning('screen_not_detected', 'No dominant green or blue screen was detected.');
        if (screenType !== 'unknown' && coverage < 0.5)
            warning('low_screen_coverage', 'The detected screen covers less than 50% of the frame.');
        if (screenType !== 'unknown' && uniformityScore < 0.75)
            warning('low_screen_uniformity', 'The screen color is not sufficiently uniform.');
        if (hotspotCells.length)
            warning('screen_hotspots', `${hotspotCells.length} bright screen region(s) detected.`);
        if (darkCells.length)
            warning('screen_dark_patches', `${darkCells.length} dark screen region(s) detected.`);
        if (unevenLighting)
            warning('uneven_screen_lighting', 'Screen illumination varies significantly across the frame.');
        const overlays = [];
        if (screenType !== 'unknown' && chromaCount > 0) {
            overlays.push({
                type: 'mask',
                points: [
                    { x: minX / width, y: minY / height },
                    { x: Math.min(1, (maxX + stride) / width), y: Math.min(1, (maxY + stride) / height) },
                ],
                label: `${screenType} screen mask`,
                color: screenType === 'green' ? '#50ff72' : '#4d8dff',
                opacity: 0.65,
                stroke_width: 2,
                fill: true,
            });
        }
        overlays.push(...hotspotCells.slice(0, 12).map((cell) => cellOverlay(cell.index, 'Hotspot', '#ffcc33')));
        overlays.push(...darkCells.slice(0, 12).map((cell) => cellOverlay(cell.index, 'Dark patch', '#ff5577')));
        return {
            module_id: this.id,
            module_name: this.label,
            confidence: round(confidence),
            frame_number: context.frameNumber,
            timestamp_ms: context.timestampMs,
            metrics: {
                screen_type: screenType,
                screen_quality_score: round(screenQualityScore),
                coverage_percentage: round(coverage * 100, 2),
                uniformity_score: round(uniformityScore),
                hotspot_count: hotspotCells.length,
                dark_patch_count: darkCells.length,
                uneven_lighting: unevenLighting,
                lighting_uniformity_score: round(lightingUniformityScore),
            },
            warnings,
            overlays,
        };
    }
}
//# sourceMappingURL=ScreenQualityAnalyzer.js.map